// Client-side CSV / XLSX export for any {columns, rows, total_row} table.
// Shared by the chat table artifact and the report exhibit tables so the
// download behavior stays identical. `xlsx` is heavy, so it is lazy
// imported inside the handler (per militao CLAUDE.md).

export interface RowExportColumn {
  key: string
  label: string
}

export interface RowExport {
  columns: RowExportColumn[]
  rows: Array<Record<string, unknown>>
  totalRow?: Record<string, unknown> | null
  title?: string | null
}

// Build a download filename like "rodela-v-rodela-large-withdrawals-
// 2026-07-12": project name, then the chart/table title, then the date so
// repeated exports don't overwrite each other. Falls back to `fallback`
// (e.g. "table" or "chart") when the title is empty.
export function tableExportFilename(
  title: string | null | undefined,
  fallback = 'table',
  projectName?: string | null,
): string {
  const today = new Date().toISOString().slice(0, 10)
  const parts = [slugify(projectName), slugify(title) || fallback, today]
  return parts.filter(Boolean).join('-')
}

function slugify(value: string | null | undefined): string {
  return (value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
}

export function downloadRowsCsv(table: RowExport, filename: string): void {
  const csv = buildRowMatrix(table)
    .map((row) => row.map(toCsvCell).join(','))
    .join('\r\n')
  // Lead with a BOM so Excel reads UTF-8 rather than mangling accents.
  const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' })
  triggerDownload(blob, filename)
}

export async function downloadRowsXlsx(
  table: RowExport,
  filename: string,
): Promise<void> {
  const XLSX = await import('xlsx')
  const worksheet = XLSX.utils.aoa_to_sheet(buildRowMatrix(table))
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(
    workbook,
    worksheet,
    sanitizeSheetName(table.title),
  )
  const buffer = XLSX.write(workbook, { type: 'array', bookType: 'xlsx' })
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
  triggerDownload(blob, filename)
}

function buildRowMatrix({ columns, rows, totalRow }: RowExport): unknown[][] {
  const header = columns.map((column) => column.label)
  const body = rows.map((row) =>
    columns.map((column) => row[column.key] ?? null),
  )
  if (totalRow) {
    body.push(columns.map((column) => totalRow[column.key] ?? null))
  }
  return [header, ...body]
}

function toCsvCell(value: unknown): string {
  if (value === null || value === undefined) return ''
  const text = String(value)
  if (/[",\r\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`
  return text
}

// Excel forbids these characters in a sheet name and caps it at 31 chars.
function sanitizeSheetName(title: string | null | undefined): string {
  const cleaned = (title ?? 'Table').replace(/[\\/?*[\]:]/g, ' ').trim()
  return (cleaned || 'Table').slice(0, 31)
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  URL.revokeObjectURL(url)
}
